<template>
  <div>
    <!-- Upload Section -->
    <div class="row mb-3">
      <div class="col text-end">
        <button
          type="button"
          class="btn btn-success btn-sm"
          data-bs-toggle="modal"
          data-bs-target="#attachmentUploadModal"
        >
          <i class="bi bi-file-earmark-plus me-1"></i> إضافة مرفق
        </button>
      </div>
    </div>

    <!-- Upload Modal -->
    <div
      class="modal fade"
      id="attachmentUploadModal"
      tabindex="-1"
      aria-labelledby="attachmentUploadModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="attachmentUploadModalLabel">إضافة مرفق جديد</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="uploadError" class="alert alert-danger">{{ uploadError }}</div>
            <div class="mb-3">
              <label class="form-label">اسم المرفق: <span class="req">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="newAttachName"
                placeholder="مثال: صورة الصك، عقد الإيجار..."
              />
            </div>
            <div class="mb-3">
              <label class="form-label">الملف (صورة أو PDF): <span class="req">*</span></label>
              <input
                type="file"
                class="form-control"
                ref="fileInput"
                accept=".png,.jpg,.jpeg,.webp,.pmb,.pdf"
                @change="onFileChange"
              />
              <div class="form-text">الأنواع المسموح بها: PNG, JPG, JPEG, WEBP, PDF</div>
            </div>
            <!-- Preview -->
            <div v-if="previewUrl" class="mt-2 text-center">
              <img
                v-if="!isPdf"
                :src="previewUrl"
                class="img-thumbnail"
                style="max-height: 150px;"
                alt="معاينة"
              />
              <div v-else class="border rounded p-3 text-muted">
                <i class="bi bi-file-earmark-pdf fs-1 text-danger"></i>
                <div class="mt-1 small">{{ selectedFileName }}</div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
              :disabled="uploading"
            >إلغاء</button>
            <button
              type="button"
              class="btn btn-success"
              @click="uploadAttachment"
              :disabled="uploading"
            >
              <span v-if="uploading"><i class="bi bi-hourglass-split me-1"></i> جاري الرفع...</span>
              <span v-else><i class="bi bi-upload me-1"></i> رفع</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <mainLoader v-if="loading" />

    <div v-else-if="!loading && attachments.length === 0" class="alert alert-info text-center">
      لا توجد مرفقات
    </div>

    <!-- Attachments Table -->
    <div v-else class="table-responsive">
      <table class="table table-bordered table-hover align-middle">
        <thead class="table-light">
          <tr>
            <th style="width: 50px;">#</th>
            <th>اسم المرفق</th>
            <th style="width: 120px;">نوع الملف</th>
            <th style="width: 150px;">معاينة</th>
            <th style="width: 100px;">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(attach, index) in attachments" :key="attach.id">
            <td>{{ index + 1 }}</td>
            <td>{{ attach.name }}</td>
            <td>
              <span v-if="attach.fileType === 'pdf'" class="badge bg-danger">
                <i class="bi bi-file-earmark-pdf me-1"></i>PDF
              </span>
              <span v-else class="badge bg-info text-dark">
                <i class="bi bi-image me-1"></i>صورة
              </span>
            </td>
            <td class="text-center">
              <a
                v-if="attach.fileType === 'pdf'"
                :href="MEDIA_URL + attach.path"
                target="_blank"
                class="btn btn-outline-danger btn-sm"
              >
                <i class="bi bi-file-earmark-pdf me-1"></i> فتح PDF
              </a>
              <a v-else :href="MEDIA_URL + attach.path" target="_blank">
                <img
                  :src="MEDIA_URL + attach.path"
                  class="img-thumbnail"
                  style="max-height: 60px; max-width: 100px; object-fit: cover;"
                  alt=""
                />
              </a>
            </td>
            <td class="text-center">
              <button
                type="button"
                class="btn btn-danger btn-sm"
                @click="deleteAttachment(attach.id)"
              >
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { toast } from '@/helpers/tools';

export default {
  name: 'PropertyAttachments',
  props: {
    propertyId: {
      type: [Number, String],
      default: null,
    },
    containerId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      uploading: false,
      attachments: [],
      newAttachName: '',
      selectedFile: null,
      selectedFileName: '',
      previewUrl: null,
      isPdf: false,
      uploadError: null,
    };
  },
  computed: {
    ...mapGetters(['MEDIA_URL']),
    apiBase() {
      if (this.containerId)
        return `/api/admin/propertyContainers/${this.containerId}/attachments`;
      return `/api/admin/properties/${this.propertyId}/attachments`;
    },
    deleteBase() {
      if (this.containerId)
        return `/api/admin/propertyContainers/attachments`;
      return `/api/admin/properties/attachments`;
    },
  },
  methods: {
    loadAttachments() {
      this.loading = true;
      this.$http
        .get(this.apiBase)
        .then(({ data }) => {
          this.attachments = data.rows;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) {
        this.selectedFile = null;
        this.previewUrl = null;
        this.isPdf = false;
        this.selectedFileName = '';
        return;
      }
      this.selectedFile = file;
      this.selectedFileName = file.name;
      const ext = file.name.split('.').pop().toLowerCase();
      this.isPdf = ext === 'pdf';
      this.previewUrl = this.isPdf ? true : URL.createObjectURL(file);
    },
    async uploadAttachment() {
      this.uploadError = null;
      if (!this.newAttachName || this.newAttachName.trim() === '') {
        this.uploadError = 'يجب إدخال اسم المرفق';
        return;
      }
      if (!this.selectedFile) {
        this.uploadError = 'يجب اختيار ملف';
        return;
      }
      this.uploading = true;
      const formData = new FormData();
      formData.append('name', this.newAttachName.trim());
      formData.append('file', this.selectedFile);
      try {
        const { data } = await this.$http.post(this.apiBase, formData);
        this.attachments.unshift(data.row);
        this.newAttachName = '';
        this.selectedFile = null;
        this.previewUrl = null;
        this.isPdf = false;
        this.selectedFileName = '';
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        const modal = window.bootstrap?.Modal?.getInstance(
          document.getElementById('attachmentUploadModal')
        );
        if (modal) modal.hide();
        toast('تم رفع المرفق بنجاح', 'success');
      } catch (err) {
        this.uploadError = 'حدث خطأ أثناء رفع الملف، يرجى المحاولة مرة أخرى';
      } finally {
        this.uploading = false;
      }
    },
    deleteAttachment(id) {
      this.$swal
        .fire({
          title: 'هل تريد تأكيد حذف المرفق؟',
          showCancelButton: true,
          confirmButtonText: 'حذف',
          cancelButtonText: 'إلغاء',
          confirmButtonColor: '#dc3741',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$http
              .delete(`${this.deleteBase}/${id}`)
              .then(() => {
                this.attachments = this.attachments.filter((a) => a.id !== id);
                toast('تم حذف المرفق بنجاح', 'success');
              })
              .catch(() => {
                toast('غير قادر على حذف المرفق');
              });
          }
        });
    },
  },
  created() {
    this.loadAttachments();
  },
};
</script>